import React, { ComponentPropsWithRef, forwardRef } from 'react';
import { Checkmark, Container, Content } from './Checkbox.styles';
import { MarginProps } from '../../../theme/styledProps';
import { Text } from '../Text/Text';
import { Box } from '../Box/Box';
import { HelperText } from '../HelperText/HelperText';
import { extractMarginProps } from '../utils/extractMarginProps';

interface CheckboxBaseProps extends ComponentPropsWithRef<'input'> {
  hideFocus?: boolean;
}

interface CheckboxBasePropsWithWrap extends CheckboxBaseProps {
  isOverflowWrap?: boolean;
}

export const CheckboxBase = forwardRef<HTMLInputElement, CheckboxBasePropsWithWrap>(
  ({ children, hideFocus, className, isOverflowWrap, ...rest }, ref) => {
    return (
      <Container as={children ? 'label' : 'div'} hideFocus={hideFocus} className={className}>
        <input ref={ref} type="checkbox" {...rest} />

        <Content css={isOverflowWrap ? { alignItems: 'flex-start' } : undefined}>
          <Checkmark css={{ marginTop: children ? 2 : 0, flexShrink: 0 }} />
          {/* minWidth: 0 lets flex children shrink so isOverflowWrap text can wrap instead of truncating */}
          <Box flex={isOverflowWrap ? '1' : undefined} minWidth={isOverflowWrap ? 0 : undefined}>
            {children}
          </Box>
        </Content>
      </Container>
    );
  },
);

export interface CheckboxProps extends ComponentPropsWithRef<'input'>, MarginProps {
  isOverflowWrap?: boolean;
  helperText?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ isOverflowWrap, children, helperText, ...rest }, ref) => {
    return (
      <Box display="flex" py="4" px={6} {...extractMarginProps(rest)}>
        <CheckboxBase
          ref={ref}
          css={isOverflowWrap ? 'flex: 1; min-width: 0' : 'flex: 1'}
          isOverflowWrap={isOverflowWrap}
          {...rest}
        >
          {(children || helperText) && (
            <>
              {children && (
                <Text fontWeight={500} pl="8" isOverflowWrap={isOverflowWrap}>
                  {children}
                </Text>
              )}
              {helperText && <HelperText ml="8">{helperText}</HelperText>}
            </>
          )}
        </CheckboxBase>
      </Box>
    );
  },
);
